import {lazy, memo, Suspense, useCallback, useContext, useMemo, useState} from 'react'

import PropTypes from 'prop-types'

import AtomCard from '@s-ui/react-atom-card'
import Context from '@s-ui/react-context'
import AnimationFadeOut from '@adv-mt/mt-animation-fade-out'
import {leadPhoneCallTarget} from '@adv-mt/mt-lead-phone-call'

import CardAdComparator from './components/cardFooter/card-ad-comparator.js'
import CardAdFinancingInfo from './components/cardFooter/card-ad-financing-info.js'
import CardAdFooter from './components/cardFooter/card-ad-footer.js'
import CardAdFooterSubscription from './components/cardFooter/card-ad-footer-subscription.js'
import CardAdDeleted from './card-ad-deleted.js'
import CardAdInfo from './card-ad-info.js'
import CardAdMedia, {cardAdMediaImageSizes, FETCHPRIORITY} from './card-ad-media.js'

const BASE_CLASS = 'mt-CardAd'
const BASE_CLASS_BASIC = 'mt-CardBasic'

const CardAdContact = lazy(() => import(/* webpackChunkName: "CardAdContact" */ './card-ad-contact.js'))

const noop = () => {}

const linkFactoryDefaultProp = ({children, ...rest} = {}) => <a {...rest}>{children}</a>

const CardAd = ({
  ad,
  cardClass = '',
  contactable = false,
  emailLeadType,
  emailListType,
  extendedTrackingData = {},
  favoriteTrackingOrigin, // TODO: put everything related to favorites together
  fetchpriority,
  financingData = {},
  forcedShowPriceIndicator = false,
  hasDiscardButton = false,
  hasLastImageContactButton = false,
  hasMenu,
  imageSize = cardAdMediaImageSizes.SMALL,
  isExternalTarget = false,
  isFavoritesList = false,
  isGridLayout = false,
  isHighlighted = false,
  isPreview = false,
  linkFactory: Link = linkFactoryDefaultProp,
  onCardAdHide = () => {},
  onClick = noop,
  pageName,
  showFavorite = true,
  showImageCounter = true,
  showIsNewInformation = false,
  showPriceDrop = false,
  showTimeAgoInformation = false,
  target,
  timeAgoInformation,
  vertical = false,
  enableComparator,
  handleSelectComparar,
  comparatorSelectedAds = []
}) => {
  const {domain} = useContext(Context)
  const config = domain.get('config')
  const {FINANCING_PARTNERS} = config.get('financing')

  const {favoriteData, financingInfo = {}, isProfessional, isSubscription, photos, title, url} = ad
  const isDeleted = favoriteData && favoriteData.adDeleted
  const [isHidden, setHidden] = useState(false)

  const onToggleFav = useCallback(
    nextFavoriteState => {
      isFavoritesList && nextFavoriteState === false && setHidden(true)
    },
    [isFavoritesList]
  )

  const instalmentInfoAvailable = Object.keys(financingInfo).length > 0

  const showFinancingFooter = isProfessional && instalmentInfoAvailable && !isPreview

  const showSubscriptionFooter = isSubscription && !showFinancingFooter
  const showSlider = !isFavoritesList
  const showCounter = showSlider && showImageCounter

  const cardAdFavProps = useMemo(
    () => ({
      ad,
      onToggleFav,
      trackingOrigin: favoriteTrackingOrigin
    }),
    [ad, favoriteTrackingOrigin, onToggleFav]
  )

  const handleOnAfterDiscard = useCallback(() => {
    setHidden(true)
  }, [])

  const handleClick = e => {
    onClick({event: e, id: ad.id, url: ad.url})
  }

  const handleCardAdHide = useCallback(() => {
    onCardAdHide({id: ad.id})
  }, [ad.id, onCardAdHide])

  const MemoCardAdMedia = useCallback(
    () => (
      <div className="mt-CardAd-mediaContainer">
        <CardAdMedia
          ad={ad}
          fetchpriority={fetchpriority}
          hasLastImageContactButton={hasLastImageContactButton}
          images={photos}
          imageSize={imageSize}
          isExternalTarget={isExternalTarget}
          isGridLayout={isGridLayout}
          isPreview={isPreview}
          linkFactory={Link}
          showImageCounter={showCounter}
          showImageSlider={showSlider}
          title={title}
          url={url}
        />
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ad]
  )

  const MemoCardAdInfo = useMemo(
    () => (
      <CardAdInfo
        ad={ad}
        cardAdFavProps={cardAdFavProps}
        contactable={contactable}
        extendedTrackingData={extendedTrackingData}
        financingData={financingData}
        forcedShowPriceIndicator={forcedShowPriceIndicator}
        hasMenu={hasMenu}
        isDeleted={isDeleted}
        isDiscardedAdsEnabled={hasDiscardButton}
        isExternalTarget={isExternalTarget}
        isGridLayout={isGridLayout}
        isPreview={isPreview}
        emailLeadType={emailLeadType}
        linkFactory={Link}
        multiprovince={extendedTrackingData.provinces}
        onAfterDiscard={handleOnAfterDiscard}
        showFavorite={showFavorite}
        showIsNewInformation={showIsNewInformation}
        showPriceDrop={showPriceDrop}
        showTimeAgoInformation={showTimeAgoInformation}
        target={target}
        timeAgoInformation={timeAgoInformation}
        vertical={vertical}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ad]
  )

  const MemoContent = useCallback(() => {
    return <div className="mt-CardAd-infoContainer">{MemoCardAdInfo}</div>
  }, [MemoCardAdInfo])

  return (
    <AnimationFadeOut
      callback={handleCardAdHide}
      extraClassNames={[cardClass, BASE_CLASS, BASE_CLASS_BASIC]}
      start={isHidden}
    >
      <>
        {isFavoritesList && isDeleted && <CardAdDeleted ad={ad} onToggleFav={onToggleFav} pageName={pageName} />}
        <AtomCard
          content={MemoContent}
          highlight={isHighlighted}
          href={ad.url}
          media={MemoCardAdMedia}
          onClick={handleClick}
          vertical={vertical}
        />
      </>
      {contactable && (
        <Suspense fallback={null}>
          <div className={`${BASE_CLASS}-contactButton`}>
            <CardAdContact
              ad={ad}
              emailLeadType={emailLeadType}
              emailListType={emailListType}
              multiprovince={extendedTrackingData.provinces}
              pageName={pageName}
              target={target}
              extendedTrackingData={extendedTrackingData}
            />
          </div>
        </Suspense>
      )}
      <CardAdFooter
        hasSmallPadding={showFinancingFooter && ad.financingInfo?.lender === FINANCING_PARTNERS.COFIDIS}
        visible={enableComparator || showFinancingFooter || showSubscriptionFooter}
        hasLeftItem={enableComparator}
        isGridLayout={isGridLayout || isFavoritesList}
      >
        {enableComparator && (
          <CardAdComparator
            ad={ad}
            enableComparator={enableComparator}
            handleSelectComparar={handleSelectComparar}
            comparatorSelectedAds={comparatorSelectedAds}
          />
        )}
        {showFinancingFooter && (
          <CardAdFinancingInfo
            adInfo={{
              adId: ad.id,
              cashPrice: ad.price,
              financedPrice: ad.financedPrice || ad.price,
              financingInfo: ad.financingInfo || {}
            }}
            financingInfo={ad.financingInfo}
            isVertical={vertical}
            seller={ad?.seller}
          />
        )}
        {showSubscriptionFooter && <CardAdFooterSubscription ad={ad} />}
      </CardAdFooter>
    </AnimationFadeOut>
  )
}

CardAd.displayName = 'CardAd'

CardAd.propTypes = {
  ad: PropTypes.object.isRequired,
  cardClass: PropTypes.string,
  contactable: PropTypes.bool,
  emailLeadType: PropTypes.string,
  emailListType: PropTypes.string,
  extendedTrackingData: PropTypes.object,
  favoriteTrackingOrigin: PropTypes.string,
  fetchpriority: PropTypes.oneOf(Object.values(FETCHPRIORITY)),
  financingData: PropTypes.object,
  forcedShowPriceIndicator: PropTypes.bool,
  hasDiscardButton: PropTypes.bool,
  hasLastImageContactButton: PropTypes.bool,
  hasMenu: PropTypes.bool,
  imageSize: PropTypes.string,
  isExternalTarget: PropTypes.bool,
  isFavoritesList: PropTypes.bool,
  isGridLayout: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  isPreview: PropTypes.bool,
  linkFactory: PropTypes.func,
  onCardAdHide: PropTypes.func,
  onClick: PropTypes.func,
  pageName: PropTypes.string,
  showFavorite: PropTypes.bool,
  showImageCounter: PropTypes.bool,
  showIsNewInformation: PropTypes.bool,
  showPriceDrop: PropTypes.bool,
  showTimeAgoInformation: PropTypes.bool,
  target: PropTypes.string,
  timeAgoInformation: PropTypes.string,
  vertical: PropTypes.bool,
  comparatorSelectedAds: PropTypes.array,
  enableComparator: PropTypes.bool,
  handleSelectComparar: PropTypes.func
}

export {cardAdMediaImageSizes as cardAdImageSizes, FETCHPRIORITY, leadPhoneCallTarget as cardAdLeadTargets}

export default memo(CardAd)
